import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from '../axiosConfig';
import '../styles/assetShareLinks.css';

function authHeaders() {
  const token = localStorage.getItem('token');
  return { Authorization: `Bearer ${token}` };
}

function absoluteShareUrl(sharePath) {
  if (!sharePath) return '';
  const origin = window.location.origin.replace(/\/$/, '');
  return `${origin}${sharePath.startsWith('/') ? sharePath : `/${sharePath}`}`;
}

function emptyDraft() {
  return {
    password: '',
    action: 'both',
    expires_at: '',
  };
}

/**
 * Independent protected links per asset.
 *
 * assets: [
 *   {
 *     key: 'digital_cover'|'print_cover'|'digital'|'print'|...,
 *     type: 'cover'|'digital'|'print'|'zip',  // API asset_type
 *     title: string,
 *     blurb?: string,
 *     bookId: number|null,
 *     available: boolean
 *   }
 * ]
 */
export default function AssetShareLinksPanel({ bookTitle, assets = [] }) {
  const [linksByKey, setLinksByKey] = useState({});
  const [drafts, setDrafts] = useState({});
  const [busyKey, setBusyKey] = useState(null);
  const [messages, setMessages] = useState({});
  const [loading, setLoading] = useState(true);
  /** Plaintext password shown after create (not stored on server) */
  const [shownPasswords, setShownPasswords] = useState({});

  const assetsSignature = (assets || [])
    .map(
      (a) =>
        `${a.key || a.type}:${a.type}:${Number(a.bookId) || ''}:${a.available ? 1 : 0}:${a.title || ''}`
    )
    .join('|');

  const slots = useMemo(
    () =>
      (assets || []).map((a, i) => ({
        key: a.key || `${a.type || 'asset'}_${i}`,
        type: a.type,
        title: a.title || a.type || 'Asset',
        blurb: a.blurb || '',
        bookId: a.bookId ? Number(a.bookId) : null,
        available: !!a.available,
      })),
    // assetsSignature tracks the meaningful fields from assets
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [assetsSignature]
  );

  const assetsKey = slots
    .map((s) => `${s.key}:${s.type}:${s.bookId || ''}:${s.available ? 1 : 0}`)
    .join('|');

  const bookIdsKey = useMemo(() => {
    const ids = slots.map((s) => s.bookId).filter(Boolean);
    return [...new Set(ids)].join(',');
  }, [assetsKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const setDraft = (slotKey, patch) => {
    setDrafts((prev) => ({
      ...prev,
      [slotKey]: { ...(prev[slotKey] || emptyDraft()), ...patch },
    }));
  };

  const messageTimers = useRef({});

  const setMessage = (slotKey, text, kind = 'ok') => {
    if (messageTimers.current[slotKey]) {
      clearTimeout(messageTimers.current[slotKey]);
      delete messageTimers.current[slotKey];
    }
    if (!text) {
      setMessages((prev) => {
        const next = { ...prev };
        delete next[slotKey];
        return next;
      });
      return;
    }
    setMessages((prev) => ({ ...prev, [slotKey]: { text, kind } }));
    // Success messages show once briefly; errors stay a bit longer
    const ms = kind === 'error' ? 5000 : 2800;
    messageTimers.current[slotKey] = setTimeout(() => {
      setMessages((prev) => {
        if (!prev[slotKey] || prev[slotKey].text !== text) return prev;
        const next = { ...prev };
        delete next[slotKey];
        return next;
      });
      delete messageTimers.current[slotKey];
    }, ms);
  };

  useEffect(() => {
    return () => {
      Object.values(messageTimers.current).forEach((t) => clearTimeout(t));
      messageTimers.current = {};
    };
  }, []);

  const loadLinks = useCallback(async () => {
    if (!bookIdsKey) {
      setLinksByKey({});
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.get(`/share/links?book_ids=${bookIdsKey}`, {
        headers: authHeaders(),
      });
      const next = {};
      (data.links || []).forEach((link) => {
        next[`${link.book_id}:${link.asset_type}`] = link;
      });
      setLinksByKey(next);
      setMessages((prev) => {
        if (!prev._panel) return prev;
        const { _panel, ...rest } = prev;
        return rest;
      });
    } catch (err) {
      console.error('Failed to load share links', err);
      setMessages((prev) => ({
        ...prev,
        _panel: {
          text: err.response?.data?.error || 'Failed to load share links',
          kind: 'error',
        },
      }));
    } finally {
      setLoading(false);
    }
  }, [bookIdsKey]);

  useEffect(() => {
    loadLinks();
  }, [loadLinks]);

  const getLinkFor = (assetType, bookId) => {
    if (!bookId || !assetType) return null;
    return linksByKey[`${bookId}:${assetType}`] || null;
  };

  const handleCreate = async (slot) => {
    if (!slot?.bookId || !slot.available) return;
    const draft = drafts[slot.key] || emptyDraft();
    if (!draft.password || draft.password.trim().length < 4) {
      setMessage(slot.key, 'Password must be at least 4 characters.', 'error');
      return;
    }
    const plainPassword = draft.password.trim();
    setBusyKey(slot.key);
    setMessage(slot.key, '');
    try {
      await axios.post(
        '/share/links',
        {
          book_id: slot.bookId,
          asset_type: slot.type,
          password: plainPassword,
          action: draft.action || 'both',
          expires_at: draft.expires_at || null,
          max_uses: null,
        },
        { headers: authHeaders() }
      );
      setShownPasswords((prev) => ({ ...prev, [slot.key]: plainPassword }));
      setDraft(slot.key, emptyDraft());
      setMessage(slot.key, 'Link created.', 'ok');
      await loadLinks();
    } catch (err) {
      setMessage(
        slot.key,
        err.response?.data?.error || err.message || 'Failed to create link',
        'error'
      );
    } finally {
      setBusyKey(null);
    }
  };

  const handleCopy = async (slot) => {
    const link = getLinkFor(slot.type, slot.bookId);
    if (!link?.share_path) return;
    const url = absoluteShareUrl(link.share_path);
    try {
      await navigator.clipboard.writeText(url);
      setMessage(slot.key, 'Link copied to clipboard.', 'ok');
    } catch {
      setMessage(slot.key, `Copy this link: ${url}`, 'ok');
    }
  };

  const handleCopyPassword = async (slot) => {
    const pwd = shownPasswords[slot.key];
    if (!pwd) return;
    try {
      await navigator.clipboard.writeText(pwd);
      setMessage(slot.key, 'Password copied to clipboard.', 'ok');
    } catch {
      setMessage(slot.key, `Password: ${pwd}`, 'ok');
    }
  };

  const handleDisable = async (slot) => {
    const link = getLinkFor(slot.type, slot.bookId);
    if (!link?.id || !link.is_active) return;
    if (
      !window.confirm(
        `Disable the ${slot.title} protected link? Recipients will no longer be able to open it.`
      )
    ) {
      return;
    }
    setBusyKey(slot.key);
    try {
      await axios.post(`/share/links/${link.id}/disable`, {}, { headers: authHeaders() });
      setShownPasswords((prev) => {
        const next = { ...prev };
        delete next[slot.key];
        return next;
      });
      setMessage(slot.key, 'Link disabled.', 'ok');
      await loadLinks();
    } catch (err) {
      setMessage(
        slot.key,
        err.response?.data?.error || err.message || 'Failed to disable link',
        'error'
      );
    } finally {
      setBusyKey(null);
    }
  };

  return (
    <section className="asl-panel" aria-label="Protected share links">
      <div className="asl-panel-head">
        <h3>Protected share links</h3>
        <p>
          {bookTitle ? (
            <>
              Create a separate password link for each file of <strong>{bookTitle}</strong>.
              Digital Cover, Print Cover, Digital PDF, and Print PDF never unlock each other.
              School users still use Manage Access.
            </>
          ) : (
            <>
              Create a separate password link for each file. Each link unlocks only that file.
              School users still use Manage Access.
            </>
          )}
        </p>
      </div>

      {messages._panel?.text ? (
        <p className={`asl-msg ${messages._panel.kind === 'error' ? 'is-error' : 'is-ok'}`}>
          {messages._panel.text}
        </p>
      ) : null}

      {loading ? (
        <p className="asl-meta">Loading links…</p>
      ) : (
        <div className="asl-grid asl-grid-4">
          {slots.map((slot) => {
            const available = !!(slot.bookId && slot.available);
            const link = getLinkFor(slot.type, slot.bookId);
            const active = !!(link && link.is_active);
            const draft = drafts[slot.key] || emptyDraft();
            const msg = messages[slot.key];
            const busy = busyKey === slot.key;
            const shareUrl = link?.share_path ? absoluteShareUrl(link.share_path) : '';
            const shownPassword = shownPasswords[slot.key] || '';

            let statusClass = 'is-missing';
            let statusText = 'No file';
            if (available && active) {
              statusClass = 'is-active';
              statusText = 'Link active';
            } else if (available && link && !link.is_active) {
              statusClass = 'is-off';
              statusText = 'Disabled';
            } else if (available) {
              statusClass = 'is-off';
              statusText = 'No link yet';
            }

            return (
              <div
                key={slot.key}
                className={`asl-block${!available ? ' is-unavailable' : ''}`}
              >
                <div className="asl-block-top">
                  <div>
                    <h4 className="asl-block-title">{slot.title}</h4>
                  </div>
                  <span className={`asl-status ${statusClass}`}>{statusText}</span>
                </div>

                {active && shareUrl ? (
                  <div className="asl-link-row">
                    <span className="asl-link-label">Share URL</span>
                    <div className="asl-link-value">
                      <input
                        type="text"
                        readOnly
                        value={shareUrl}
                        aria-label={`${slot.title} share URL`}
                      />
                      <button
                        type="button"
                        className="asl-btn asl-btn-secondary"
                        disabled={busy}
                        onClick={() => handleCopy(slot)}
                      >
                        Copy
                      </button>
                    </div>
                  </div>
                ) : null}

                {shownPassword ? (
                  <div className="asl-link-row">
                    <span className="asl-link-label">Password</span>
                    <div className="asl-link-value">
                      <input
                        type="text"
                        readOnly
                        value={shownPassword}
                        aria-label={`${slot.title} password`}
                        className="asl-password-shown"
                      />
                      <button
                        type="button"
                        className="asl-btn asl-btn-secondary"
                        onClick={() => handleCopyPassword(slot)}
                      >
                        Copy
                      </button>
                    </div>
                  </div>
                ) : null}

                {available ? (
                  <div className="asl-form">
                    <label>
                      {active ? 'New password (creates a fresh link)' : 'Access password'}
                      <input
                        type="text"
                        autoComplete="off"
                        spellCheck={false}
                        placeholder="Min. 4 characters"
                        value={draft.password}
                        onChange={(e) => setDraft(slot.key, { password: e.target.value })}
                      />
                    </label>
                    <div className="asl-form-row">
                      <label>
                        Allowed action
                        <select
                          value={draft.action}
                          onChange={(e) => setDraft(slot.key, { action: e.target.value })}
                        >
                          <option value="both">View & download</option>
                          <option value="view">View only</option>
                          <option value="download">Download only</option>
                        </select>
                      </label>
                      <label>
                        Expiry (optional)
                        <input
                          type="date"
                          value={draft.expires_at}
                          onChange={(e) => setDraft(slot.key, { expires_at: e.target.value })}
                        />
                      </label>
                    </div>
                  </div>
                ) : (
                  <p className="asl-meta">Upload this file on Edit Book before creating a link.</p>
                )}

                <div className="asl-actions">
                  <button
                    type="button"
                    className="asl-btn asl-btn-primary"
                    disabled={!available || busy}
                    onClick={() => handleCreate(slot)}
                  >
                    {busy ? 'Working…' : active ? 'Create new link' : 'Create link'}
                  </button>
                  <button
                    type="button"
                    className="asl-btn asl-btn-danger"
                    disabled={!active || busy}
                    onClick={() => handleDisable(slot)}
                  >
                    Disable
                  </button>
                </div>

                {msg?.text ? (
                  <p className={`asl-msg ${msg.kind === 'error' ? 'is-error' : 'is-ok'}`}>
                    {msg.text}
                  </p>
                ) : null}
              </div>
            );
          })}
        </div>
      )}

      <p className="asl-hint">
        Tip: share the URL and the password through different channels. Disabling one link does not
        affect the others.
      </p>
    </section>
  );
}
